"use client";

import { Suspense, use, useEffect, useState } from "react";
import { Link } from "@/src/lib/next-compat";
import { useNavigate, useParams } from "react-router-dom";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { normalizeGeneratedText } from "@/lib/normalize-generated-text";
import { coerceQuizOptionLabel } from "@/lib/quiz-option-label";

import { getPlacementQuizApiV1PlacementQuizGet, submitPlacementQuizApiV1PlacementQuizSubmitPost } from "@/src/api/sdk.gen";

interface QuizQuestion {
    id: number;
    question_text: string;
    options: (string | { label?: string; text?: string; value?: string })[];
    level_name: string;
}

interface QuizData {
    id: string | number;
    title: string;
    description: string;
    questions: QuizQuestion[];
    version: number;
    courseUuid?: string | null;
}

interface SubmissionResult {
    placementLevel: string;
    score: number;
}

function normalizePlacementQuestion(question: QuizQuestion): QuizQuestion {
    return {
        ...question,
        question_text: normalizeGeneratedText(question.question_text),
        level_name: normalizeGeneratedText(question.level_name),
        options: (question.options || []).map((option) => {
            if (typeof option === "string") return normalizeGeneratedText(option);
            return {
                ...option,
                label: option.label ? normalizeGeneratedText(option.label) : option.label,
                text: option.text ? normalizeGeneratedText(option.text) : option.text,
                value: option.value ? normalizeGeneratedText(option.value) : option.value,
            };
        }),
    };
}

function QuizContent({ params }: { params?: Promise<{ id: string }> } = {}) {
    const routeParams = useParams<{ id: string }>();
    const courseId = routeParams.id || "";
    const navigate = useNavigate();

    const [quiz, setQuiz] = useState<QuizData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isAlreadyTaken, setIsAlreadyTaken] = useState(false);

    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({});
    const [showResults, setShowResults] = useState(false);
    const [submissionResult, setSubmissionResult] = useState<SubmissionResult | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    useEffect(() => {
        const controller = new AbortController();

        const fetchQuiz = async () => {
            try {
                const isNumericCourseId = /^\d+$/.test(courseId);
                const { data: resData, error: reqErr, response } = await getPlacementQuizApiV1PlacementQuizGet({
                    query: { course_id: isNumericCourseId ? Number.parseInt(courseId, 10) : (courseId as any) } as any,
                    signal: controller.signal,
                });

                if (reqErr || !resData) {
                    if (response?.status === 403) {
                        const errBody = (reqErr || {}) as { alreadyDetermined?: boolean };
                        if (errBody.alreadyDetermined) {
                            setIsAlreadyTaken(true);
                            return;
                        }
                    }
                    if (response?.status === 404) {
                        throw new Error("No approved placement quiz found for this course.");
                    }
                    throw new Error("Failed to fetch placement quiz.");
                }

                const data = resData as unknown as QuizData & { courseUuid?: string | null };
                const normalizedQuestions = Array.isArray(data.questions)
                    ? data.questions.map((q: QuizQuestion) => normalizePlacementQuestion(q))
                    : [];

                if (normalizedQuestions.length === 0) {
                    throw new Error("No questions available for this quiz.");
                }

                setQuiz({
                    ...data,
                    title: normalizeGeneratedText(data.title ?? "Placement Quiz"),
                    description: normalizeGeneratedText(data.description ?? ""),
                    questions: normalizedQuestions,
                });

                if (data.courseUuid && courseId !== data.courseUuid) {
                    navigate(`/courses/${data.courseUuid}/quiz`);
                }
            } catch (err) {
                if (err instanceof DOMException && err.name === "AbortError") return;
                setError(err instanceof Error ? err.message : "An unexpected error occurred");
            } finally {
                setLoading(false);
            }
        };

        void fetchQuiz();

        return () => controller.abort();
    }, [courseId]);

    const questions = quiz?.questions ?? [];
    const question = questions[currentQuestion];
    const selectedOption = question ? selectedAnswers[question.id] : undefined;

    const handleOptionSelect = (questionId: number, optionLabel: string) => {
        setSelectedAnswers((prev) => ({
            ...prev,
            [questionId]: optionLabel,
        }));
        if (submitError) setSubmitError(null);
    };

    const submitQuiz = async () => {
        if (!quiz) return;

        setIsSubmitting(true);
        setSubmitError(null);
        try {
            const formattedAnswers = Object.entries(selectedAnswers).map(([qId, ans]) => ({
                questionId: parseInt(qId, 10),
                answer: ans,
            }));

            const { data: resData, error: reqErr, response } = await submitPlacementQuizApiV1PlacementQuizSubmitPost({
                body: {
                    quizId: Number(quiz.id),
                    answers: formattedAnswers,
                } as any,
            });

            const data = (resData || reqErr || {}) as unknown as SubmissionResult & {
                error?: string;
                alreadyDetermined?: boolean;
            };

            if (reqErr) {
                if (data.alreadyDetermined) {
                    setIsAlreadyTaken(true);
                    setShowResults(false);
                    return;
                }
                throw new Error(data.error ?? "Failed to submit quiz results.");
            }

            setSubmissionResult({
                placementLevel: data.placementLevel,
                score: data.score,
            });
            setShowResults(true);
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : "Failed to submit quiz.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNext = async () => {
        if (!question || selectedOption === undefined) return;

        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion((prev) => prev + 1);
            return;
        }

        await submitQuiz();
    };

    const handleRetake = () => {
        setCurrentQuestion(0);
        setSelectedAnswers({});
        setShowResults(false);
        setSubmissionResult(null);
        setSubmitError(null);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
        );
    }

    if (isAlreadyTaken) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <CheckCircle className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Assessment Completed</h2>
                    <p className="text-gray-600 mb-6">
                        You have already completed the placement assessment for this course.
                    </p>
                    <Link
                        to={`/courses/${courseId}`}
                        className="block w-full py-3 bg-primary text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors"
                    >
                        Return to Course
                    </Link>
                </div>
            </div>
        );
    }

    if (error || !quiz || questions.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 mb-2">{error ?? "Quiz Not Found"}</h2>
                    <Link
                        to={`/courses/${courseId}`}
                        className="text-indigo-600 font-semibold hover:text-indigo-700"
                    >
                        Return to Course
                    </Link>
                </div>
            </div>
        );
    }

    if (showResults && submissionResult) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Quiz Complete!</h2>
                    <p className="text-gray-600 mb-2">
                        You scored{" "}
                        <span className="font-bold text-green-600">
                            {Math.round(submissionResult.score)}%
                        </span>
                    </p>
                    <p className="text-gray-600 mb-6">
                        Placed at the{" "}
                        <span className="font-bold text-indigo-600">{submissionResult.placementLevel}</span>{" "}
                        level.
                    </p>

                    <Link
                        to={`/courses/${courseId}`}
                        className="block w-full py-3 bg-primary text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors"
                    >
                        Return to Course
                    </Link>
                </div>
            </div>
        );
    }

    if (!question) return null;

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto">
                <div className="mb-8 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{quiz.title}</h1>
                        <p className="text-gray-500 text-sm">
                            Question {currentQuestion + 1} of {questions.length}
                        </p>
                    </div>
                    <div className="px-3 py-1 bg-indigo-100 text-indigo-700 font-bold rounded-full text-sm">
                        {question.level_name}
                    </div>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-2 mb-8">
                    <div
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(currentQuestion / questions.length) * 100}%` }}
                    />
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-8">
                        <h2 className="text-xl font-bold text-gray-900 mb-6 leading-relaxed">
                            {question.question_text}
                        </h2>

                        <div className="space-y-3">
                            {question.options.map((option, index) => {
                                const label = coerceQuizOptionLabel(option);
                                const isSelected = selectedOption === label;

                                return (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => handleOptionSelect(question.id, label)}
                                        className={`w-full text-left p-4 rounded-xl border-2 font-medium transition-all ${
                                            isSelected
                                                ? "border-primary bg-indigo-50 text-indigo-700 ring-1 ring-primary"
                                                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>

                        {submitError ? (
                            <p className="mt-4 text-sm text-red-600" role="alert">
                                {submitError}
                            </p>
                        ) : null}
                    </div>

                    <div className="bg-gray-50 p-6 border-t border-gray-100 flex justify-between items-center gap-4">
                        <button
                            type="button"
                            onClick={handleRetake}
                            disabled={isSubmitting}
                            className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-900 disabled:opacity-50"
                        >
                            Start Over
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleNext()}
                            disabled={selectedOption === undefined || isSubmitting}
                            className={`px-6 py-3 rounded-xl font-bold transition-all ${
                                selectedOption !== undefined && !isSubmitting
                                    ? "bg-primary text-white hover:bg-indigo-700 shadow-lg hover:shadow-xl"
                                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                            }`}
                        >
                            {isSubmitting ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : currentQuestion < questions.length - 1 ? (
                                "Next Question"
                            ) : (
                                "Submit Quiz"
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function QuizPage(props?: {
    params?: Promise<{ id: string }>;
}) {
    return (
        <Suspense
            fallback={
                <div className="min-h-screen flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
            }
        >
            <QuizContent params={props?.params} />
        </Suspense>
    );
}
